import { useCallback, useMemo, useState } from 'react';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { formatDistance } from 'date-fns';

import type { TabOnClickEvent } from '@simplywallst/ui-core';
import { useMount, TabList, TabPanel, Tab } from '@simplywallst/ui-core';
import { Badge } from '@simplywallst/ui-core/next';

import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import { PortfolioPageHeader } from '../../../../components/PortfolioPageHeader';
import { DemoHoldingsPanel } from '../../../../components/HoldingsPanel';

import { CreateNewPortfolioBanner } from './CreateNewPortfolioBanner';
import { RenderUntilExpiry } from '@/components/RenderUntilExpiry';

import {
  ROUTE_PORTFOLIO_DEMO_RETURNS,
  ROUTE_PORTFOLIO_DEMO_HOLDINGS,
  ROUTE_PORTFOLIO_DEMO_NARRATIVES,
  ROUTE_PORTFOLIO_DEMO_ANALYSIS,
  ROUTE_PORTFOLIO_DEMO_DIVIDENDS,
  ROUTE_PORTFOLIO_DEMO_UPDATES,
} from '@/constants/routes';
import { redirect } from 'redux-first-router';
import { useDispatch } from 'react-redux';
import { useFlag } from '@simplywallst/unleash';

const DemoDividendPanel = loadable(
  () => import('../../../../components/DividendPanel'),
  {
    resolveComponent: (c) => c.DividendPanel,
  }
);

const DemoAnalysisPanel = loadable(
  () => import('../../../../components/AnalysisPanel'),
  {
    resolveComponent: (c) => c.DemoAnalysisPanel,
  }
);

const DemoNarrativePanel = loadable(
  () => import('../../../../components/NarrativePanel'),
  {
    resolveComponent: (c) => c.DemoNarrativePanel,
  }
);

const ReturnsPanel = loadable(
  () => import('../../../../components/ReturnsPanel'),
  {
    resolveComponent: (c) => c.ReturnsPanel,
  }
);

const UpdatesPanel = loadable(
  () => import('../../../../components/UpdatesPanel'),
  {
    resolveComponent: (c) => c.UpdatesPanel,
  }
);

export type PortfolioTab =
  | 'holdings'
  | 'analysis'
  | 'dividend'
  | 'narratives'
  | 'returns'
  | 'updates';

const getRouteType = (tab: PortfolioTab) => {
  switch (tab) {
    case 'returns':
      return ROUTE_PORTFOLIO_DEMO_RETURNS;
    case 'holdings':
      return ROUTE_PORTFOLIO_DEMO_HOLDINGS;
    case 'narratives':
      return ROUTE_PORTFOLIO_DEMO_NARRATIVES;
    case 'analysis':
      return ROUTE_PORTFOLIO_DEMO_ANALYSIS;
    case 'dividend':
      return ROUTE_PORTFOLIO_DEMO_DIVIDENDS;
    case 'updates':
      return ROUTE_PORTFOLIO_DEMO_UPDATES;
  }
};

type Props = {
  portfolio: Portfolio;
  initialPanel: PortfolioTab;
};

export const PopulatedState = ({ portfolio, initialPanel }: Props) => {
  const updatesFlag = useFlag('mono-portfolio-updates-tab');
  const hasAccessToUpdatesPanel = updatesFlag.ready && updatesFlag.enabled;

  const [selectedPanel, setSelectedPanel] = useState<PortfolioTab>(
    initialPanel ?? 'holdings'
  );
  const dispatch = useDispatch();

  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: 'populatedState',
  });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  const timeSinceLastUpdate = useMemo(() => {
    return formatDistance(new Date(portfolio.updatedAt), new Date(), {
      addSuffix: true,
    });
  }, [portfolio.updatedAt]);

  const handleTabClick = useCallback(
    (event: TabOnClickEvent<PortfolioTab>) => {
      const tab = event.target.value;
      const type = getRouteType(tab);
      if (!tab || !type) return;
      setSelectedPanel(tab);
      dispatch(
        redirect({
          type,
        })
      );
    },
    [dispatch]
  );

  return (
    <Track>
      <MiniPriceChartDefinitions />
      <div className="mb-x3 grid w-full">
        <CreateNewPortfolioBanner />

        <div className="w-full max-w-screen-xl justify-self-center md:mb-x2 lg:px-x3">
          <div className="mx-x2 mt-x2 flex flex-wrap items-start justify-between gap-x2 lg:mx-x0">
            <div className="grid gap-x0_5">
              <PortfolioPageHeader portfolio={portfolio} className="px-x0" />
              <div className="text-tiny text-p-white/30">
                Updated{' '}
                <time className="text-p-white/50">{timeSinceLastUpdate}</time>
              </div>
            </div>
          </div>

          <TabList
            templateColumns="repeat(6, max-content)"
            className="w-screen overflow-x-auto scrollbar-hidden lg:w-full"
          >
            <Tab<PortfolioTab>
              value="holdings"
              selected={selectedPanel === 'holdings'}
              onClick={handleTabClick}
              styleType="subnav"
            >
              Holdings
            </Tab>
            <Tab<PortfolioTab>
              value="returns"
              selected={selectedPanel === 'returns'}
              onClick={handleTabClick}
              styleType="subnav"
            >
              <div className="pointer-events-none flex items-center gap-x1">
                Returns
                <RenderUntilExpiry
                  startedFrom="2025-05-22"
                  expiresIn={{ months: 1 }}
                >
                  <Badge tone="attention">NEW</Badge>
                </RenderUntilExpiry>
              </div>
            </Tab>
            <Tab<PortfolioTab>
              value="narratives"
              selected={selectedPanel === 'narratives'}
              onClick={handleTabClick}
              styleType="subnav"
            >
              Narratives
            </Tab>
            {hasAccessToUpdatesPanel && (
              <Tab<PortfolioTab>
                value="updates"
                selected={selectedPanel === 'updates'}
                onClick={handleTabClick}
                styleType="subnav"
              >
                Updates
              </Tab>
            )}
            <Tab<PortfolioTab>
              value="dividend"
              selected={selectedPanel === 'dividend'}
              onClick={handleTabClick}
              styleType="subnav"
            >
              Dividends
            </Tab>
            <Tab<PortfolioTab>
              value="analysis"
              selected={selectedPanel === 'analysis'}
              onClick={handleTabClick}
              styleType="subnav"
            >
              Analysis
            </Tab>
          </TabList>
          <hr className="m-x0" />
        </div>
        <TabPanel<PortfolioTab>
          value="holdings"
          selected={selectedPanel === 'holdings'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <DemoHoldingsPanel portfolio={portfolio} />
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="narratives"
          selected={selectedPanel === 'narratives'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <DemoNarrativePanel portfolio={portfolio} />
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="analysis"
          selected={selectedPanel === 'analysis'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <DemoAnalysisPanel portfolio={portfolio} />
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="dividend"
          selected={selectedPanel === 'dividend'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <DemoDividendPanel portfolio={portfolio} />
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="returns"
          selected={selectedPanel === 'returns'}
          className="w-full"
        >
          <ReturnsPanel portfolio={portfolio} />
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="updates"
          selected={selectedPanel === 'updates'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <UpdatesPanel demoPortfolioId={portfolio.id} />
        </TabPanel>
      </div>
    </Track>
  );
};
